import { Box, Stack, Typography } from '@mui/material';
import { compact } from 'lodash';
import { useMemo } from 'react';

import { SubtleLabelItem } from '@/components/display/SubtleLabelItem/SubtleLabelItem';
import { Card } from '@/components/layout/Card/Card';
import { GroupedHeaderList } from '@/components/lists/GroupedHeaderList/GroupedHeaderList';
import { RichListItem } from '@/components/lists/RichListItem/RichListItem';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { DispositiveProvisionsListView } from '@/modules/dispositiveProvisions/DispositiveProvisionsListView/DispositiveProvisionsListView';
import { CONTROLLING_PERSONS_LABEL } from '@/modules/entities/EntitySubforms/utils/shared/controllingParties.utils';
import { controllingPartyEntityKindOptions } from '@/modules/entities/testamentaryEntities/shared/testamentaryEntity.constants';
import { testamentaryEntityKindToDisplayName } from '@/modules/entities/testamentaryEntities/testamentaryEntities.utils';
import { ContextPrimaryClient } from '@/modules/household/contexts/householdDetails.context';
import { TrustAdvisorsList } from '@/modules/trusts/TrustAdvisorsList/TrustAdvisorsList';
import { TrustBeneficiariesList } from '@/modules/trusts/TrustBeneficiariesList/TrustBeneficiariesList';
import { TrustTrusteesList } from '@/modules/trusts/TrustTrusteesList/TrustTrusteesList';

import { TestamentaryEntitySummaryPanel_TestamentaryEntityFragment } from './graphql/TestamentaryEntitySummaryPanel.generated';
import { TestamentaryEntityTabs } from './TestamentaryEntitySummaryPanel.types';

interface Props {
  testamentaryEntity: TestamentaryEntitySummaryPanel_TestamentaryEntityFragment;
  hasAliveSpouse: boolean;
  taxStatus: string;
  firstDeathPrimaryClientId: string;
  assetLocation: string | null;
  primaryClients: ContextPrimaryClient[];
}

const controllingPartySet = new Set(controllingPartyEntityKindOptions);

export function useTestamentaryEntityPanels({
  testamentaryEntity,
  hasAliveSpouse,
  taxStatus,
  firstDeathPrimaryClientId,
  assetLocation,
  primaryClients,
}: Props) {
  const {
    kind,
    displayName,
    beneficiaries,
    beneficiariesAfterBothGrantorDeaths,
    trustees,
    successorTrustees,
    trustAdvisors,
    controllingParties,
    description,
  } = testamentaryEntity;

  return useMemo(() => {
    const isControllingPartyEntity = controllingPartySet.has(kind);
    const firstDeathPrimaryClient = primaryClients.find(
      (primaryClient) => primaryClient.id === firstDeathPrimaryClientId
    );
    const createdUponDeathOfDisplayName = firstDeathPrimaryClient?.displayName;

    const beneficiaryPanels = compact([
      {
        display: hasAliveSpouse
          ? 'Access after first death'
          : 'Access after death',
        value: TestamentaryEntityTabs.AccessAfterDeath,
        component: function AccessAfterDeath() {
          return (
            <TrustBeneficiariesList
              isTestamentaryEntity={true}
              beneficiariesGroups={[
                {
                  heading: undefined,
                  items: beneficiaries || [],
                },
              ]}
              emptyStateLabels={{
                heading: 'Specify accessors after death',
                description: 'No accessors have been specified for this entity',
                buttonText: 'Add accessors',
              }}
              testamentaryEntityInitialTab="beneficiaries"
            />
          );
        },
      },
      // Only show "Access after both deaths" for two-client households
      hasAliveSpouse && {
        display: 'Access after both deaths',
        value: TestamentaryEntityTabs.AccessAfterBothDeaths,
        component: function AccessAfterBothDeaths() {
          return (
            <TrustBeneficiariesList
              isTestamentaryEntity={true}
              beneficiariesGroups={[
                {
                  heading: undefined,
                  items: beneficiariesAfterBothGrantorDeaths || [],
                },
              ]}
              emptyStateLabels={{
                heading: 'Specify accessors after both deaths',
                description: 'No accessors have been specified for this entity',
                buttonText: 'Add accessors',
              }}
              testamentaryEntityInitialTab="beneficiaries-after-both-die"
            />
          );
        },
      },
    ]);

    return compact([
      {
        display: 'Summary',
        value: TestamentaryEntityTabs.Summary,
        component: function Summary() {
          return (
            <Box>
              <Stack spacing={2}>
                <SubtleLabelItem label="Legal name" value={displayName} />
                <SubtleLabelItem
                  label="Entity type"
                  value={testamentaryEntityKindToDisplayName(
                    kind,
                    createdUponDeathOfDisplayName
                  )}
                />
                <SubtleLabelItem
                  label="Created upon death of"
                  value={createdUponDeathOfDisplayName ?? EMPTY_CONTENT_HYPHEN}
                />
                <SubtleLabelItem label="Tax status" value={taxStatus} />
                <SubtleLabelItem label="Asset location" value={assetLocation} />
              </Stack>
              {description && (
                <Box mt={2}>
                  <Typography variant="body-s">{description}</Typography>
                </Box>
              )}
            </Box>
          );
        },
      },
      ...beneficiaryPanels,
      {
        display: isControllingPartyEntity ? 'Controlling parties' : 'Trustees',
        value: TestamentaryEntityTabs.Trustees,
        component: function Trustees() {
          if (isControllingPartyEntity) {
            return (
              <Stack>
                <GroupedHeaderList<{ id: string; heading: string }>
                  rowGroups={[
                    {
                      heading: CONTROLLING_PERSONS_LABEL,
                      items:
                        controllingParties?.map((cp) => ({
                          id: cp.id,
                          heading:
                            cp.individual?.legalName ??
                            cp.organization?.name ??
                            EMPTY_CONTENT_HYPHEN,
                        })) ?? [],
                      noItemsText: 'No controlling parties',
                    },
                  ]}
                  ItemComponent={({ item }) => <RichListItem {...item} />}
                />
              </Stack>
            );
          }

          return (
            <Stack spacing={2}>
              <TrustTrusteesList
                trustees={trustees ?? []}
                successorTrustees={successorTrustees ?? []}
              />
              {trustAdvisors && trustAdvisors.length > 0 && (
                <Card variant="filled" sx={{ p: 2 }}>
                  <TrustAdvisorsList trustAdvisors={trustAdvisors} />
                </Card>
              )}
            </Stack>
          );
        },
      },
      {
        display: 'Dispositions',
        value: TestamentaryEntityTabs.Disposition,
        component: function Dispositions() {
          return (
            <DispositiveProvisionsListView
              firstDeathPrimaryClientId={firstDeathPrimaryClientId}
            />
          );
        },
      },
    ]);
  }, [
    assetLocation,
    beneficiaries,
    beneficiariesAfterBothGrantorDeaths,
    controllingParties,
    description,
    displayName,
    firstDeathPrimaryClientId,
    hasAliveSpouse,
    kind,
    primaryClients,
    successorTrustees,
    taxStatus,
    trustAdvisors,
    trustees,
  ]);
}
